"use client";

import universalBtnBg from "@/_assets/images/Universal btn bg.png";
import simImg from "@/_assets/images/sim.png";
import SpinnerMini from "@/components/my-ui/fallbacks/SpinnerMini";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import CopyButton from "@/components/features/sims/CopyButton";
import { formatDateTime } from "@/helpers/formatDateTime";
import { cn } from "@/lib/utils";
import { useAvailableEsimsForTopup } from "@/hooks/useAvailableEsimsForTopup";
import { AvailableEsim } from "@/types/purchase/AvailableEsimsForTopup";
import { ChevronRight, Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useMemo, useState } from "react";

interface PropsType {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onBuyNewEsim: () => void;
  onContinueWithExistingEsim: (iccid: string) => void;
  isContinuing?: boolean;
  availableEsims?: AvailableEsim[] | null;
}

function SelectEsimModal({
  open,
  setOpen,
  onBuyNewEsim,
  onContinueWithExistingEsim,
  isContinuing = false,
  availableEsims: passedAvailableEsims,
}: PropsType) {
  const t = useTranslations("Shared.selectEsimModal");
  const [selectedSimId, setSelectedSimId] = useState("");

  const shouldFetch = !passedAvailableEsims && open;
  const {
    data: queryAvailableEsims,
    isPending: queryIsPending,
    error,
  } = useAvailableEsimsForTopup(shouldFetch);

  const availableEsims = passedAvailableEsims ?? queryAvailableEsims;
  const isPending = useMemo(() => {
    return passedAvailableEsims ? false : queryIsPending;
  }, [passedAvailableEsims, queryIsPending]);

  function handleBuyNewEsim() {
    setOpen(false);
    onBuyNewEsim();
  }

  function handleContinue() {
    const selectedSim = availableEsims?.find(
      (sim) => sim.sim_id === selectedSimId,
    );
    if (!selectedSim) return;
    onContinueWithExistingEsim(selectedSim.iccid);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (isContinuing) return;
        setOpen(value);
        if (!value) setSelectedSimId("");
      }}
    >
      <DialogContent className="max-w-[687px] gap-5">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>

        {/* Buy a new eSIM */}
        <Button
          type="button"
          variant="ghost"
          onClick={handleBuyNewEsim}
          disabled={isContinuing}
          className="relative h-[59px] w-full gap-3 overflow-hidden rounded-[24px] px-6 py-0 text-white hover:bg-transparent hover:text-white hover:opacity-90 md:px-6 md:py-0"
        >
          <Image
            src={universalBtnBg}
            alt=""
            fill
            sizes="687px"
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-black/50" />

          <span className="relative z-10 flex items-center gap-2 text-[18px] font-700">
            <Plus size={20} className="shrink-0" />
            {t("buyNewEsim")}
          </span>

          <Image
            src={simImg}
            alt=""
            className="relative z-10 h-11 w-auto shrink-0"
          />
        </Button>

        {/* divider */}
        <div className="relative flex items-center justify-center py-1">
          <div className="absolute inset-x-0 border-t border-border" />
          <span className="relative bg-background px-3 text-[14px] font-600 uppercase tracking-wide text-[#9CA3AF]">
            {t("orSelectCurrent")}
          </span>
        </div>

        {/* current eSIMs */}
        {isPending && (
          <div className="flex min-h-[120px] items-center justify-center">
            <SpinnerMini />
          </div>
        )}

        {!isPending && error && (
          <p className="rounded-sm bg-destructive/10 p-3 text-center text-sm text-destructive">
            {error.message}
          </p>
        )}

        {!isPending && !error && availableEsims?.length === 0 && (
          <p className="rounded-sm bg-primary/10 p-3 text-center text-sm">
            {t("noEsimsFound")}
          </p>
        )}

        {!isPending && !error && availableEsims && availableEsims.length > 0 && (
          <RadioGroup
            value={selectedSimId}
            onValueChange={setSelectedSimId}
            className={cn(
              "barMini grid max-h-[320px] grid-cols-1 gap-3 overflow-y-auto pe-1 sm:grid-cols-2",
              isContinuing && "pointer-events-none opacity-60",
            )}
          >
            {availableEsims.map((sim) => (
              <div
                key={sim.sim_id}
                onClick={() => setSelectedSimId(sim.sim_id)}
                className={cn(
                  "flex cursor-pointer flex-col gap-3 rounded-lg border-2 p-3 transition-colors hover:border-primary",
                  selectedSimId === sim.sim_id
                    ? "border-primary bg-primary/5"
                    : "border-border",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-600 leading-tight">
                      {sim.last_bundle}
                    </p>
                  </div>
                  <RadioGroupItem
                    value={sim.sim_id}
                    checked={selectedSimId === sim.sim_id}
                  />
                </div>

                <div className="flex flex-col gap-0.5">
                  <p className="text-xs text-muted-foreground">
                    {t("iccid")}
                  </p>
                  <div
                    className="flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="break-all text-sm font-500">{sim.iccid}</p>
                    <CopyButton text={sim.iccid} />
                  </div>
                </div>

                <div className="flex flex-col gap-0.5">
                  <p className="text-xs text-muted-foreground">
                    {t("purchaseDate")}
                  </p>
                  <p className="text-sm font-500">
                    {formatDateTime(sim.date_created)}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        )}

        <Button
          className="h-[52px] w-full rounded-[12px]"
          disabled={!selectedSimId || isContinuing}
          onClick={handleContinue}
        >
          {isContinuing ? (
            <SpinnerMini />
          ) : (
            <>
              {t("continue")}
              <ChevronRight size={18} />
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default SelectEsimModal;
